/**
 * /walk/sermon-notes/:date? — full-screen sermon-note composer.
 *
 * WYSIWYG editing (TipTap) over Markdown storage: the family formats
 * visually, we persist Markdown. The header (Sunday / title / link)
 * prefills the title + link from the weekly sermon scrape (KV) when
 * starting a fresh note for the current Sunday. Saving upserts by
 * (member, date), so re-opening a Sunday edits the same note.
 *
 * Edits autosave: the note persists in the background ~1.2s after the
 * family stops typing (and best-effort on unmount) so content is never
 * lost. The note's identity is its server-assigned `id` — the upsert
 * keys on (member, date), never the title, so the title is free to
 * change without orphaning or duplicating the row.
 */
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type LatestSermon,
  type SermonNote,
  type SermonNotesResponse,
} from '../lib/api';
import { sermonNotesQueryOptions } from '../lib/queries';
import { TextField } from '../components/TextField';
import { ScreenHeader } from '../components/ScreenHeader';
import { Button } from '../components/Button';

/** How long to wait after the last edit before autosaving. */
const AUTOSAVE_DELAY_MS = 1200;

const SermonNoteEditor = lazy(() => import('../components/SermonNoteEditor'));

/** ISO date of the most recent Sunday (today, if today is Sunday). */
function lastSundayISO(d = new Date()): string {
  const x = new Date(d);
  x.setDate(x.getDate() - x.getDay()); // getDay(): 0 = Sunday
  return toISODate(x);
}
function toISODate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/** Photo IDs referenced inline, scraped back out of the saved Markdown
 *  (images embed as /api/photos/<id>/large). Kept for lifecycle/cleanup. */
function extractPhotoIds(md: string): string[] {
  const ids = new Set<string>();
  const re = /\/api\/photos\/([^/\s)]+)\//g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(md))) ids.add(decodeURIComponent(m[1]!));
  return [...ids];
}

export default function SermonNotesPage() {
  const { date } = useParams();
  const navigate = useNavigate();
  const q = useQuery(sermonNotesQueryOptions());
  const [selectedDate, setSelectedDate] = useState(date ?? lastSundayISO());

  return (
    <>
      <ScreenHeader title="Sermon Notes" subtitle="What you heard on Sunday." />
      <div className="px-5 pt-4 pb-6 space-y-4">
        <label className="block">
          <span className="kicker text-sage">Sunday</span>
          <TextField
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value || lastSundayISO())}
            className="mt-1"
          />
        </label>

        {q.isLoading ? (
          <div className="card p-4 text-soft text-sm">Loading…</div>
        ) : (
          <EditorForm
            key={selectedDate}
            selectedDate={selectedDate}
            existing={q.data?.notes.find((n) => n.mine && n.date === selectedDate)}
            latestSermon={q.data?.latestSermon ?? null}
            onSaved={() => navigate('/walk')}
          />
        )}
      </div>
    </>
  );
}

function EditorForm({
  selectedDate,
  existing,
  latestSermon,
  onSaved,
}: {
  selectedDate: string;
  existing: SermonNote | undefined;
  latestSermon: LatestSermon | null;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  // Prefill the title from the weekly scrape only for a brand-new note on
  // the current Sunday; an existing note always wins. (The sermon link is
  // no longer edited here — we preserve any URL an old note already had.)
  const isThisSunday = selectedDate === lastSundayISO();
  const [title, setTitle] = useState(
    existing?.sermonTitle ?? (isThisSunday ? (latestSermon?.title ?? '') : ''),
  );
  const [body, setBody] = useState(existing?.body ?? '');
  const [visibility, setVisibility] = useState<'private' | 'family'>(
    existing?.visibility ?? 'family',
  );
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  // The request body the server upserts. Stringified, it doubles as our
  // dirty-check key: identical snapshot ⇒ nothing to persist.
  const payload = {
    date: selectedDate,
    sermonTitle: title.trim() || null,
    sermonUrl: existing?.sermonUrl?.trim() || null,
    body,
    photoIds: extractPhotoIds(body),
    visibility,
  };
  const snapshot = JSON.stringify(payload);

  const hasContent = title.trim().length > 0 || body.trim().length > 0;
  // A snapshot we'd actually send: there's something worth keeping. Held
  // in a ref so the unmount flush sees the latest value without
  // re-subscribing.
  const savable = hasContent ? snapshot : null;
  const savableRef = useRef(savable);
  savableRef.current = savable;

  // Last snapshot the server has. Seeded from the note we opened so an
  // untouched note never triggers a spurious save. A ref (not state):
  // mutating it shouldn't re-render, but the setStatus that always
  // accompanies it does, so reads stay fresh.
  const lastSavedRef = useRef(snapshot);
  const dirty = snapshot !== lastSavedRef.current;

  // Merge the saved note into the cached list so /walk reflects it
  // without a refetch (and without remounting this keyed editor). Re-sort
  // by createdAt desc to match the server order — NOT prepend, which would
  // bump an edited note to the top (ordering by last edit instead of
  // creation). A brand-new note has the newest createdAt and lands first;
  // an edited note keeps its creation position.
  const cacheNote = useCallback(
    (note: SermonNote) => {
      qc.setQueryData<SermonNotesResponse>(['sermon-notes'], (prev) => {
        if (!prev) return prev;
        const notes = [note, ...prev.notes.filter((n) => n.id !== note.id)].sort(
          (a, b) => b.createdAt - a.createdAt,
        );
        return { ...prev, notes };
      });
    },
    [qc],
  );

  const persist = useCallback(
    async (snap: string) => {
      setStatus('saving');
      try {
        const { note } = await api.post<{ note: SermonNote }>(
          '/sermon-notes',
          JSON.parse(snap),
        );
        lastSavedRef.current = snap;
        cacheNote(note);
        setStatus('saved');
        return note;
      } catch (e) {
        setErrorMsg((e as Error).message);
        setStatus('error');
        throw e;
      }
    },
    [cacheNote],
  );

  // Debounced autosave: fire once the family pauses, skipping clean,
  // empty, or half-typed-link states.
  useEffect(() => {
    if (!savable || savable === lastSavedRef.current) return;
    const t = setTimeout(() => void persist(savable).catch(() => {}), AUTOSAVE_DELAY_MS);
    return () => clearTimeout(t);
  }, [savable, persist]);

  // Best-effort flush on unmount (e.g. navigating Back within the
  // debounce window) so the last keystrokes aren't lost.
  useEffect(
    () => () => {
      const pending = savableRef.current;
      if (pending && pending !== lastSavedRef.current) {
        void api.post('/sermon-notes', JSON.parse(pending)).catch(() => {});
      }
    },
    [],
  );

  async function onSave() {
    if (!savable) return;
    if (!dirty) return onSaved(); // already autosaved — just close
    try {
      await persist(savable);
      onSaved();
    } catch {
      /* error surfaced inline; stay on the page */
    }
  }

  const saveStatus = !hasContent
    ? ''
    : status === 'saving'
      ? 'Saving…'
      : status === 'error'
        ? "Couldn't save — we'll retry as you edit"
        : dirty
          ? 'Unsaved changes…'
          : 'All changes saved';

  return (
    <div className="space-y-3">
      <label className="block">
        <span className="kicker text-sage">Sermon</span>
        <TextField
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Sermon title"
          className="mt-1"
        />
      </label>

      <Suspense
        fallback={<div className="card p-4 text-soft text-sm">Loading editor…</div>}
      >
        <SermonNoteEditor initialMarkdown={body} onChange={setBody} />
      </Suspense>

      {status === 'error' && (
        <p className="text-sm text-oxblood">Couldn't save: {errorMsg}</p>
      )}

      {/* One compact control row: visibility on the left, save status +
          a small Done on the right — keeps the note area tall. */}
      <div className="flex items-center justify-between gap-3">
        <fieldset className="flex items-center gap-3">
          <Radio
            name="visibility"
            checked={visibility === 'private'}
            onChange={() => setVisibility('private')}
            label="🔒 Private"
          />
          <Radio
            name="visibility"
            checked={visibility === 'family'}
            onChange={() => setVisibility('family')}
            label="👪 Family"
          />
        </fieldset>
        <div className="flex items-center gap-2 shrink-0">
          <span
            className={`text-xs ${status === 'error' ? 'text-oxblood' : 'text-soft'}`}
            aria-live="polite"
          >
            {saveStatus}
          </span>
          <Button
            size="sm"
            disabled={!savable}
            busy={status === 'saving'}
            onClick={() => void onSave()}
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}

function Radio({
  name,
  checked,
  onChange,
  label,
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-1.5 text-sm text-ink cursor-pointer">
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onChange}
        className="accent-oxblood"
      />
      {label}
    </label>
  );
}
